import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkle } from "lucide-react";
import { waLink, DEFAULT_WA_TEXT } from "../../lib/constants";
import { track } from "../../lib/tracking";

const EASE = [0.22, 1, 0.36, 1];
const lines = ["Stop Shaving", "Your Underarms", "Again and Again."];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute -top-32 -right-40 w-[34rem] h-[34rem] rounded-full bg-champagne-light/50 blur-3xl" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-8 items-center min-h-[calc(100svh-72px)] py-14 md:py-20">
        <div className="relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="flex items-center gap-3 text-[0.65rem] md:text-xs font-mono uppercase tracking-[0.3em] text-teal/70"
          >
            <Sparkle className="w-3.5 h-3.5" strokeWidth={1.5} />
            Krystal Skin And Laser Clinic · Ameerpet, Hyderabad
          </motion.p>

          <h1 className="mt-7 font-display font-light tracking-tight leading-[0.98] text-ink text-[13.5vw] sm:text-6xl md:text-7xl xl:text-[5.4rem]">
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-teal" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-7 max-w-xl text-base md:text-lg font-light leading-relaxed text-ink/65"
          >
            10-session Underarm Laser Hair Reduction Package — now{" "}
            <span className="font-semibold text-teal">₹22,500</span> instead of{" "}
            <span className="price-strike text-wine/80">₹35,000</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.82, ease: EASE }}
            className="mt-6 flex flex-wrap items-center gap-4"
          >
            <span
              data-testid="hero-savings-badge"
              className="inline-flex items-center rounded-full bg-teal px-5 py-2 font-mono text-xs md:text-sm tracking-[0.2em] text-champagne-light"
            >
              ₹12,500 SAVINGS
            </span>
            <p className="text-sm font-light text-ink/60 max-w-[16rem]">
              Plus 1 complimentary maintenance session every year for up to 10 years.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => { track("book_package_click", { placement: "hero" }); onBook(); }}
              data-testid="hero-book-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-teal px-9 py-4 text-sm font-bold uppercase tracking-[0.16em] text-ivory transition-colors duration-300 hover:bg-teal-800"
            >
              Book My Package
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </button>
            <a
              href={waLink(DEFAULT_WA_TEXT)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { placement: "hero" })}
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-teal/35 px-9 py-4 text-sm font-bold uppercase tracking-[0.16em] text-teal transition-colors duration-300 hover:bg-teal hover:text-ivory"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              Chat on WhatsApp
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-6 text-xs font-light tracking-wide text-ink/45"
            data-testid="hero-urgency-line"
          >
            Limited-period package offer. Terms and eligibility apply.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-champagne/60 aspect-[4/5] max-h-[70vh] w-full shadow-[0_40px_80px_-40px_rgba(144,41,141,0.35)] bg-teal-900">
            <motion.video
              src="/media/offer-reel.mp4"
              poster="/media/clinic-room.jpeg"
              autoPlay
              muted
              loop
              playsInline
              style={{ y: imgY }}
              className="h-[115%] w-full object-cover"
              data-testid="hero-video"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-teal-900/25 via-transparent to-transparent" aria-hidden="true" />
          </div>
          <motion.div
            style={{ y: cardY }}
            className="absolute bottom-4 left-4 md:-bottom-6 md:-left-10 rounded-2xl border border-champagne/60 bg-ivory/95 backdrop-blur px-6 py-5 shadow-[0_24px_50px_-24px_rgba(26,29,32,0.3)] animate-float-slow"
            data-testid="hero-price-card"
          >
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-teal/60">Special package price</p>
            <p className="mt-1 font-display text-4xl md:text-5xl font-light text-teal">₹22,500</p>
            <p className="mt-1 text-xs font-light text-ink/50">
              <span className="price-strike text-wine/70">₹35,000</span> · 10 sessions + annual maintenance
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
